import { useCallback, useEffect, useState } from "react";
import {
  isWebPushEnabled,
  listenForForegroundMessages,
  registerDevice,
} from "../services/pushNotifications";
import type { ForegroundPushMessage } from "../types/push";
import { PUSH_RECEIVED_EVENT } from "./useNotifications";

/**
 * Registers the web push device once after login (only if permission was
 * already granted; never prompts) and exposes the latest foreground message so
 * the UI can show an in-app toast.
 */
export function usePushNotifications(isLoggedIn: boolean) {
  const [message, setMessage] = useState<ForegroundPushMessage | null>(null);

  useEffect(() => {
    if (!isLoggedIn || !isWebPushEnabled()) return;
    let unsubscribe: () => void = () => {};
    let cancelled = false;

    registerDevice();
    listenForForegroundMessages((m) => {
      setMessage(m);
      // Let the notification bell refresh its unread count immediately.
      window.dispatchEvent(new Event(PUSH_RECEIVED_EVENT));
    }).then((unsub) => {
      if (cancelled) unsub();
      else unsubscribe = unsub;
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [isLoggedIn]);

  const clear = useCallback(() => setMessage(null), []);
  return { message, clear };
}

export default usePushNotifications;
